import { Button, Description, Label, Modal, toast } from '@heroui/react';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { CheckboxButtonGroup } from '@heroui-pro/react';
import { trpc } from '@/lib/trpc';
import { useAppSelector } from '@/stores/hooks';
import { useEnums } from '@hs/shared';
import { handleTRPCError } from '@/utils/error-handler';
import { RacDialogTriggerAnchor } from '@/components/RacDialogTriggerAnchor';

export interface AddSpaceAppsModalProps {
  open: boolean;
  spaceId: string;
  existingAppIds: string[];
  /** Sheet 内 overlay portal 容器，避免 Modal 被外层 Sheet 标记 inert */
  portalContainer?: HTMLElement | null;
  onCancel: () => void;
  onSuccess: () => void;
}

export function AddSpaceAppsModal({
  open,
  spaceId,
  existingAppIds,
  portalContainer,
  onCancel,
  onSuccess,
}: AddSpaceAppsModalProps) {
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const isHydrated = useAppSelector((state) => state.auth.isHydrated);
  const { getLabel } = useEnums({ trpc: trpc as any, modules: ['app'] });

  const [selectedAppIds, setSelectedAppIds] = useState<string[]>([]);

  const { data: appsData, error: appsError } = trpc.bizApp.listGrouped.useQuery(
    { groupBy: 'type' },
    { enabled: open && isHydrated && isAuthenticated }
  );

  useEffect(() => {
    if (appsError) handleTRPCError(appsError);
  }, [appsError]);

  useEffect(() => {
    if (!open) {
      setSelectedAppIds([]);
    }
  }, [open]);

  const addAppsMutation = trpc.spaceApps.addAppsToSpace.useMutation({
    onSuccess: () => {
      toast.success('添加成功');
      onSuccess();
    },
    onError: (error) => {
      handleTRPCError(error);
    },
  });

  const appGroups = useMemo(() => {
    const groups = (appsData && 'groups' in appsData ? appsData.groups : []) ?? [];
    const existingSet = new Set(existingAppIds);
    return groups
      .map((group: any) => ({
        key: group.key,
        items: group.items.filter((app: any) => {
          const appId = app.id || app.publicId || '';
          return appId && !existingSet.has(appId);
        }),
      }))
      .filter((group) => group.items.length > 0);
  }, [appsData, existingAppIds]);

  const allAvailableApps = useMemo(
    () => appGroups.flatMap((group: any) => group.items),
    [appGroups]
  );

  const handleSelectionChange = (value: string[]) => {
    setSelectedAppIds(value);
  };

  const handleSubmit = async () => {
    if (!spaceId) {
      toast.danger('空间ID不存在');
      return;
    }
    if (selectedAppIds.length === 0) {
      toast.warning('请先选择要添加的应用');
      return;
    }
    try {
      await addAppsMutation.mutateAsync({ spaceId, appIds: selectedAppIds });
    } catch {
      // handled in onError
    }
  };

  const isLoading = addAppsMutation.isPending;

  return (
    <Modal isOpen={open} onOpenChange={(next) => !next && onCancel()}>
      <RacDialogTriggerAnchor />
      <Modal.Backdrop
        isDismissable={false}
        UNSTABLE_portalContainer={portalContainer ?? undefined}
      >
        <Modal.Container placement="center" scroll="inside" size="lg">
          <Modal.Dialog>
            <Modal.Header className="gap-1">
              <Modal.Heading>为空间添加应用</Modal.Heading>
              <Description>仅展示当前空间尚未添加的应用，可多选。</Description>
            </Modal.Header>

            <Modal.Body className="gap-4 p-2">
              <div className="max-h-[60vh] min-h-0 overflow-y-auto p-2">
                {!appsError && allAvailableApps.length > 0 ? (
                  <CheckboxButtonGroup
                    className="grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3"
                    layout="grid"
                    name="space-apps-select"
                    value={selectedAppIds}
                    onChange={handleSelectionChange}
                    variant="secondary"
                  >
                    {appGroups.map((group: any) => {
                      const groupKey = typeof group.key === 'string' ? group.key : String(group.key);
                      const groupLabel = getLabel('app', 'type', groupKey);
                      return (
                        <Fragment key={groupKey}>
                          <Label className="col-span-full text-base font-semibold">{groupLabel}</Label>
                          {group.items.map((app: any) => {
                            const appId = app.id || app.publicId || '';
                            if (!appId) return null;
                            return (
                              <CheckboxButtonGroup.Item key={appId} value={appId}>
                                <CheckboxButtonGroup.Indicator />
                                <CheckboxButtonGroup.ItemContent>
                                  <div className="flex flex-col gap-1">
                                    <Label>{app.name}</Label>
                                    {app.description ? (
                                      <Description className="line-clamp-2 text-xs">
                                        {app.description}
                                      </Description>
                                    ) : null}
                                  </div>
                                </CheckboxButtonGroup.ItemContent>
                              </CheckboxButtonGroup.Item>
                            );
                          })}
                        </Fragment>
                      );
                    })}
                  </CheckboxButtonGroup>
                ) : null}
                {appsError && (
                  <div className="text-destructive py-10 text-center">加载应用列表失败，请刷新重试</div>
                )}
                {!appsError && allAvailableApps.length === 0 && (
                  <div className="text-muted-foreground py-10 text-center">当前没有可添加的应用</div>
                )}
              </div>
            </Modal.Body>

            <Modal.Footer>
              <Button variant="outline" onClick={onCancel}>
                取消
              </Button>
              <Button onClick={handleSubmit} isDisabled={isLoading}>
                {isLoading ? '提交中...' : '确认'}
              </Button>
            </Modal.Footer>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
}

